import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface DateTimeRangeValue {
  fromDate: Date;
  toDate: Date;
}

export type CompareToOption = "previous-shift" | "previous-day" | "avg-last-7-days" | "previous-hour" | null;
export type TargetSourceOption = "plan" | "historical-avg" | "machine-targets" | null;

interface DateTimeRangeState {
  value: DateTimeRangeValue;
  selectedPresetKey: string | null;
  compareToEnabled: boolean;
  compareToOption: CompareToOption;
  targetSourceEnabled: boolean;
  targetSourceOption: TargetSourceOption;
  setValue: (val: DateTimeRangeValue) => void;
  setSelectedPresetKey: (key: string | null) => void;
  setCompareToEnabled: (enabled: boolean) => void;
  setCompareToOption: (option: CompareToOption) => void;
  setTargetSourceEnabled: (enabled: boolean) => void;
  setTargetSourceOption: (option: TargetSourceOption) => void;
}

export const useDateTimeRangeStore = create<DateTimeRangeState>()(
  persist(
    (set) => ({
      // Default to the last 24 hours — a zero-width range (now..now)
      // renders every time-scoped page empty on first visit.
      value: {
        fromDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
        toDate: new Date(),
      },
      selectedPresetKey: "last24hours",
      compareToEnabled: false,
      compareToOption: null,
      targetSourceEnabled: false,
      targetSourceOption: null,
      setValue: (val) => set({ value: val }),
      setSelectedPresetKey: (key) => set({ selectedPresetKey: key }),
      setCompareToEnabled: (enabled) => set({ compareToEnabled: enabled }),
      setCompareToOption: (option) => set({ compareToOption: option }),
      setTargetSourceEnabled: (enabled) => set({ targetSourceEnabled: enabled }),
      setTargetSourceOption: (option) => set({ targetSourceOption: option }),
    }),
    {
      name: "date-time-range-store",
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const data = JSON.parse(str);
          return {
            state: {
              ...data.state,
              value: {
                fromDate: new Date(data.state.value.fromDate),
                toDate: new Date(data.state.value.toDate),
              },
            },
            version: data.version,
          };
        },
        setItem: (name, newValue) => {
          localStorage.setItem(name, JSON.stringify(newValue));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
    }
  )
);
